import { HttpClient } from '@angular/common/http';
import { computed, EventEmitter, inject, Injectable, Injector, signal } from '@angular/core';

import { customAlphabet } from 'nanoid';
import { catchError, lastValueFrom, throwError } from 'rxjs';
import { ActionStack } from '../features/ui/contextmenu/context-menu.component';
import { Engine } from '../engine/engine';
import { calculateVolume } from '../engine/helpers';
import { environment } from '../config/editor-environment';
import { AssemblyLoaderService } from './assembly-loader.service';
import {
  ActionType,
  FlowStep,
  FlowStepInner,
  LoadCasesFlowStep,
  LoadCaseType,
  LoadType,
  ManufacturingMethod,
  PartType,
  ProjectType
} from '../models/enums';
import { GeometryWorkerService } from './geometry/geometry-worker.service';
import { Project } from '../models/interfaces';
import { BoundaryConditionItem } from '../models/loadcase.model';
import { AssistantChatComponent } from '../features/assistant-chat/assistant-chat.component';
import { KeyboardShortcutsService } from './keyboard-shortcuts.service';
import { ProgressService } from './progress.service';
import { ProjectConfigurationService } from './project-configuration.service';
import { autoSaveTickAction } from './auto-save-tick.util';
import { deriveActiveCase, deriveActiveView } from './session-active-case.util';
import { projectHasRunResults, ResultsLockProjectSlice } from './results-lock.util';
import { ReactionForcesService } from './reaction-forces.service';
import { SettingsAndPresetsService } from './settings-and-presets.service';
import { TemplateModalService } from './modal/modal.service';
import { ExampleProjectCopyModalComponent } from '../features/layout/modals/example-project-copy-modal.component';
import { AuthService } from './auth/auth.service';
const nanoid = customAlphabet('1234567890abcdef', 10);

@Injectable({
  providedIn: 'root'
})
export class SessionService {
  http = inject(HttpClient);

  public progressService = inject(ProgressService);
  public geometryWorker = inject(GeometryWorkerService);
  public keyboardShortcuts = inject(KeyboardShortcutsService);

  // Sub-services
  private projectConfigurationService = inject(ProjectConfigurationService);
  private assemblyLoaderService = inject(AssemblyLoaderService);
  private reactionForcesService = inject(ReactionForcesService);
  private settingsAndPresetsService = inject(SettingsAndPresetsService);
  private modalService = inject(TemplateModalService);
  private injector = inject(Injector);
  private _authService: AuthService | null = null;

  private get authService(): AuthService {
    if (!this._authService) {
      this._authService = this.injector.get(AuthService);
    }
    return this._authService;
  }

  // ─── Properties that remain on SessionService ───

  // Flow
  public flowStep: FlowStep = FlowStep.MATERIALS;
  public flowStepInner: number = FlowStepInner.NONE;

  // AI Flow
  public aiBoundaryConditions: BoundaryConditionItem[] = [];

  // Enums access for template
  public FlowStepEnum: typeof FlowStep = FlowStep;
  public LoadCasesFlowStepEnum: typeof LoadCasesFlowStep = LoadCasesFlowStep;
  public ActionTypeEnum: typeof ActionType = ActionType;

  public LoadCaseTypeEnum: typeof LoadCaseType = LoadCaseType;
  public LoadTypeEnum: typeof LoadType = LoadType;

  public loadCaseOpened = false;

  public emitter = new EventEmitter();

  // Engine
  public engine?: Engine;

  public wizardActivated = false;

  public saveInterval?: any;

  public beamSettings = {
    loadType: LoadType.FORCE,
    remotePoint: [NaN, NaN, NaN],
    forceComponent: [0, 0, 0],
    momentComponent: [0, 0, 0],
    faces: [],
    accelerationNonStructuralMass: 0
  };

  // Global PubSub
  public pubsub = new EventEmitter<any>();

  // Context Menu
  public contextMenu: ActionStack = {
    enabled: false,
    actions: []
  };

  // Example project modal guard
  private exampleProjectModalOpen = false;
  private exampleProjectInitialSaveCompleted = false;

  // Auto run dialog
  public openRunDialog = false;

  // Assistant Chat
  public assistantChatComponent?: AssistantChatComponent;

  // ─── Unified progress delegation ───

  public get overallProgress(): number {
    return this.progressService.getCurrentProgress();
  }

  public get currentPhase(): string {
    return this.progressService.getCurrentPhase();
  }

  public get progressMessage(): string {
    return this.progressService.getCurrentMessage();
  }

  // ─── ProjectConfigurationService delegation ───

  get project_id(): string | null | undefined {
    return this.projectConfigurationService.project_id;
  }
  set project_id(val: string | null | undefined) {
    this.projectConfigurationService.project_id = val;
  }

  get other_user_id(): string | null | undefined {
    return this.projectConfigurationService.other_user_id;
  }
  set other_user_id(val: string | null | undefined) {
    this.projectConfigurationService.other_user_id = val;
  }

  get project(): Project {
    return this.projectConfigurationService.project;
  }
  set project(val: Project) {
    this.projectConfigurationService.project = val;
  }

  // ─── Multi-manufacturing-method: active case pointer ───

  /**
   * Editor-local pointer to the currently-selected manufacturing case.
   * Not persisted, not in URL (per design v1 decision §4).
   * Reset to the first case whenever a new project is loaded via `setProject`.
   */
  readonly activeMethodId = signal<string | null>(null);

  /**
   * The currently-active ManufacturingCase, derived from `activeMethodId`
   * and the loaded project's `manufacturingMethods[]` array. Returns
   * undefined before a project is loaded or if the project has no
   * manufacturing cases (legacy or SoftJaws/Mold projects).
   *
   * Reactive for activeMethodId changes. Project reassignment
   * re-triggers this computed via the explicit activeMethodId.set() call
   * in `setProject` below.
   */
  readonly activeCase = computed(() =>
    deriveActiveCase(this.project?.manufacturingMethods, this.activeMethodId()),
  );

  /**
   * Multi-manufacturing-method: methodIds of cases whose inputs have been
   * edited since the last Run. Displayed as a "stale — re-run to update"
   * badge next to the case in the Results page's case list, so the user
   * never mistakes old results for fresh ones after tweaking parameters.
   *
   * Cleared on project load (new session starts fresh) and on Run
   * (results will reflect the edits after the run finishes).
   */
  readonly dirtyMethodIds = signal<ReadonlySet<string>>(new Set());

  /** Mark a specific case as dirty (stale) so its results need a re-run. */
  markCaseDirty(methodId: string | null | undefined): void {
    if (!methodId) return;
    this.dirtyMethodIds.update((prev) => {
      if (prev.has(methodId)) return prev;
      const next = new Set(prev);
      next.add(methodId);
      return next;
    });
  }

  /** Clear the dirty set (called on Run + on project load). */
  clearDirtyCases(): void {
    this.dirtyMethodIds.set(new Set());
    // Suppress dirty marking for the first auto-save after a project load.
    // The load process (normalizeToNewShape, mergeChildOptimization, etc.)
    // mutates `this.project` which causes the auto-save diff to fire, but
    // those aren't user edits — they're structural migrations that shouldn't
    // mark cases as stale.
    this._suppressNextDirtyMark = true;
    // Re-arm the results-protection lock: on project load there may be fresh
    // results to protect; after a Run the upcoming results deserve the same.
    this.resultsEditUnlocked.set(false);
  }

  /** @internal */
  _suppressNextDirtyMark = false;

  isCaseDirty(methodId: string | null | undefined): boolean {
    if (!methodId) return false;
    return this.dirtyMethodIds().has(methodId);
  }

  /**
   * Results-protection lock: set once the user confirms the "editing will
   * outdate existing results" dialog. Session-only by design — re-armed by
   * `clearDirtyCases()` (project load + Run).
   */
  readonly resultsEditUnlocked = signal(false);

  /**
   * True when run-input sections should sit behind the edit-setup confirm:
   * the project has results from a previous run and the user has not
   * confirmed the unlock yet. View-only sessions are excluded — their
   * inputs are already disabled, so there is nothing to gate.
   */
  configLocked(): boolean {
    return (
      projectHasRunResults(this.project as ResultsLockProjectSlice) &&
      !this.resultsEditUnlocked() &&
      !this.isViewOnlyPermission()
    );
  }

  /**
   * View object exposing per-method fields routed through the active case.
   * Intended replacement for direct `session.project.manufacturingMethod` /
   * `session.project.millingVector` / etc. reads in the Manufacturing block
   * (Increment 4). Legacy reads continue to work against the flat mirror
   * (V2 strategy from the design doc).
   */
  readonly activeView = computed(() => deriveActiveView(this.activeCase()));

  get projectImages(): { image?: string; image_optimized?: string; image_optimized_parametric?: string; image_fea_stress?: string; image_fea_displacement?: string } {
    return this.projectConfigurationService.projectImages;
  }
  set projectImages(val: { image?: string; image_optimized?: string; image_optimized_parametric?: string; image_fea_stress?: string; image_fea_displacement?: string }) {
    this.projectConfigurationService.projectImages = val;
  }

  get sharing(): any {
    return this.projectConfigurationService.sharing;
  }
  set sharing(val: any) {
    this.projectConfigurationService.sharing = val;
  }

  get downloadLinks(): any {
    return this.projectConfigurationService.downloadLinks;
  }
  set downloadLinks(val: any) {
    this.projectConfigurationService.downloadLinks = val;
  }

  get bdfJob(): {
    status: string;
    s3Key?: string;
    localPath?: string;
    filename?: string;
    source?: 'organic' | 'prismatic' | 'analysis';
    progress?: number;
    stage?: string;
  } | null {
    return this.projectConfigurationService.bdfJob;
  }
  set bdfJob(val: {
    status: string;
    s3Key?: string;
    localPath?: string;
    filename?: string;
    source?: 'organic' | 'prismatic' | 'analysis';
    progress?: number;
    stage?: string;
  } | null) {
    this.projectConfigurationService.bdfJob = val;
  }

  get softJawsResultUrl(): string | undefined {
    return this.projectConfigurationService.softJawsResultUrl;
  }
  set softJawsResultUrl(v: string | undefined) {
    this.projectConfigurationService.softJawsResultUrl = v;
  }

  get isExampleProject(): boolean {
    return this.projectConfigurationService.isExampleProject;
  }
  set isExampleProject(val: boolean) {
    this.projectConfigurationService.isExampleProject = val;
  }

  get copyingExampleProject(): boolean {
    return this.projectConfigurationService.copyingExampleProject;
  }
  set copyingExampleProject(val: boolean) {
    this.projectConfigurationService.copyingExampleProject = val;
  }

  get isViewOnlyPermission(): () => boolean {
    return this.projectConfigurationService.isViewOnlyPermission;
  }

  get isEditablePermission(): () => boolean {
    return this.projectConfigurationService.isEditablePermission;
  }

  get isSoftJawsProject(): () => boolean {
    return this.projectConfigurationService.isSoftJawsProject;
  }

  get isMoldProject(): () => boolean {
    return this.projectConfigurationService.isMoldProject;
  }

  /** See ProjectConfigurationService.fetchMasterChildren. */
  fetchMasterChildren(): ReturnType<ProjectConfigurationService['fetchMasterChildren']> {
    return this.projectConfigurationService.fetchMasterChildren();
  }

  /** See ProjectConfigurationService.mergeChildOptimizationIntoMaster. */
  async mergeChildOptimization(childId: string): Promise<Record<string, unknown> | null> {
    const doc = await this.projectConfigurationService.mergeChildOptimizationIntoMaster(childId);
    // Also capture the child's manufacturingMethod so the Results-page
    // summary block / helpers reflect the correct method on initial load
    // (engine-initializer path) as well as subsequent swaps.
    if (doc) {
      // The backend's findOneForGUI response nests the flat fields under
      // `config` (see project.service.ts). Desktop NeDB docs flatten them
      // at the top level. Read both paths so the same code handles cloud
      // + desktop children.
      const config = (doc as { config?: Record<string, unknown> }).config;
      const flat = doc as Record<string, unknown>;
      const pick = <T>(key: string): T | undefined =>
        (config?.[key] as T | undefined) ?? (flat[key] as T | undefined);

      this.activeResultsMethod =
        (pick<ManufacturingMethod>('manufacturingMethod') as ManufacturingMethod | undefined) ?? null;

      // Capture the child's helper-relevant fields into the dedicated
      // `activeResultsHelpers` slot. We MUST NOT write them onto
      // `session.project` directly — the flat fields must stay pinned
      // to `activeMethodId` or the next `commitActiveCaseToArray` will
      // corrupt the active case (the Results-tab swap would overwrite
      // the Manufacturing tab's active case's data on save).
      const millingVector = pick<{ xp: boolean; yp: boolean; zp: boolean; xm: boolean; ym: boolean; zm: boolean }>('millingVector');
      const drawDirectionVector = pick<{ xp: boolean; yp: boolean; zp: boolean; xm: boolean; ym: boolean; zm: boolean }>('drawDirectionVector');
      const symmetry = pick<{ xy: boolean; xz: boolean; yz: boolean }>('symmetry');
      const minimumToolSize = pick<number>('minimumToolSize');
      this.activeResultsHelpers = {
        millingVector: millingVector ? { ...millingVector } : undefined,
        drawDirectionVector: drawDirectionVector ? { ...drawDirectionVector } : undefined,
        symmetry: symmetry ? { ...symmetry } : undefined,
        minimumToolSize,
      };
    }
    return doc;
  }

  /**
   * Multi-manufacturing-method: which child's results are currently shown
   * on the Results page. Forwarded from ProjectConfigurationService so the
   * template can bind `session.activeResultsChildId` directly.
   */
  get activeResultsChildId(): string | null {
    return this.projectConfigurationService.activeResultsChildId;
  }

  /**
   * Whether a results swap is currently in progress. The Results page binds
   * its loading overlay to this flag. True while the engine is torn down
   * and the new child's mesh is being fetched + rebuilt.
   */
  public switchingResultsInProgress = false;
  public switchingResultsProgress = signal<{ stage: string; percent: number } | null>(null);

  /**
   * Hot-swap which child's results are shown on the Results page without
   * reloading the editor route.
   *
   * Lifecycle:
   *  1. Fetch the child project document via HTTP
   *  2. Merge its `optimization` URL block + `materials` into the master's
   *     projectConfig singleton (so downstream services read the new URLs)
   *  3. Tear down the existing optimized mesh in the 3D engine
   *  4. Trigger the optimized-assembly reload pipeline (fetch binary →
   *     decode → setOptimizedAssembly → initOptimizedMesh)
   *  5. Re-run `calculateVolumeAndMass` so the sidebar reflects the new
   *     child's weight
   *
   * Safe to call repeatedly. Defensive try/catch keeps the editor usable
   * if any step fails — the overlay will disappear and the previous case
   * remains displayed.
   */
  /**
   * Queued target for a pending swap. If the user clicks a new case while
   * a swap is still running, we remember the latest target and fire it
   * once the current swap completes.
   */
  private pendingSwitchChildId: string | null = null;

  /**
   * Per-child cache of the expensive fetch results for the Results page.
   * Populated after the first successful swap for each childId so that
   * subsequent swaps (user clicks back and forth between cases) avoid
   * re-fetching the binary mesh / JSON loadcase assets.
   *
   * Cleared on `setProject` (new project loaded).
   */
  /**
   * Manufacturing method of the currently-displayed child on the Results
   * page. Separate from `activeMethodId` (which tracks the editor's
   * currently-edited case) so Results-page swaps don't corrupt the flat
   * scratch-pad / case labels.
   */
  public activeResultsMethod: ManufacturingMethod | null = null;

  /**
   * Helper geometry (drill bits, draw-direction arrow, symmetry planes)
   * specific to the currently-displayed child on the Results page.
   *
   * These fields MUST NOT be written onto `session.project` directly —
   * the project-level flat fields (`millingVector`, `drawDirectionVector`,
   * `symmetry`, `manufacturingMethod`) must stay in lock-step with
   * `activeMethodId`, otherwise `commitActiveCaseToArray` on the next
   * save will capture the wrong values into the active case and
   * permanently corrupt it (same class of bug that corrupted case labels
   * earlier).
   *
   * `ResultsVisualizationService.initializeEngine` reads these with a
   * fallback to the master's flat fields for legacy single-case projects.
   */
  public activeResultsHelpers: {
    millingVector?: { xp: boolean; yp: boolean; zp: boolean; xm: boolean; ym: boolean; zm: boolean };
    drawDirectionVector?: { xp: boolean; yp: boolean; zp: boolean; xm: boolean; ym: boolean; zm: boolean };
    symmetry?: { xy: boolean; xz: boolean; yz: boolean };
    minimumToolSize?: number;
  } | null = null;

  /**
   * Per-child cache of the expensive binary mesh buffers. Caching the
   * `info` JSON is intentionally NOT done: `getOptimizedAssemblyInfo` has
   * load-bearing side effects (clears `loadedLoadCases`, rebuilds
   * `loadCaseUrls`, recomputes `resultsAreVibrations`, populates URLs on
   * each engine info entry). Skipping it silently breaks per-loadcase
   * loading after the first view of a case. The binaries are the only
   * thing worth caching for mf-case-switch latency.
   */
  private resultsCacheByChildId = new Map<
    string,
    {
      organic?: ArrayBuffer;
      parametric?: ArrayBuffer;
    }
  >();

  async switchActiveChildResults(childId: string): Promise<void> {
    if (this.projectConfigurationService.activeResultsChildId === childId) return;

    // If another swap is already running, remember this as the "latest"
    // target. The running swap will pick it up in its `finally` block.
    if (this.switchingResultsInProgress) {
      this.pendingSwitchChildId = childId;
      return;
    }

    this.switchingResultsInProgress = true;
    this.switchingResultsProgress.set({ stage: 'Loading case data...', percent: 10 });
    // Suppress dirty marking: merging a child's optimization data into the
    // master mutates session.project which triggers auto-save, but it's not
    // a user edit — it's a results-viewing swap.
    this._suppressNextDirtyMark = true;
    try {
      this.switchingResultsProgress.set({ stage: 'Fetching optimization...', percent: 30 });
      const childDoc = await this.mergeChildOptimization(childId);
      if (childDoc) {
        this.switchingResultsProgress.set({ stage: 'Rebuilding meshes...', percent: 60 });
        // Reload the optimized meshes for the new child.
        await this.reloadOptimizedMeshesForActiveChild();
        this.switchingResultsProgress.set({ stage: 'Complete', percent: 100 });
      }
    } catch (err) {
      console.error('[Session] switchActiveChildResults failed:', err);
    } finally {
      // The merge mutated session.project (optimization URLs + materials)
      // which would trigger the auto-save diff check. Snapshot the current
      // state as "last saved" so the interval doesn't PUT this swap back
      // to the backend — viewing a child's results is not a user edit.
      this.lastSavedProject = JSON.stringify(this.project);
      this.switchingResultsInProgress = false;
      this.switchingResultsProgress.set(null);
      // If the user clicked a different case during the swap, run the
      // latest target next. Only the most recent click wins — intermediate
      // clicks are dropped.
      const next = this.pendingSwitchChildId;
      if (next && next !== this.projectConfigurationService.activeResultsChildId) {
        this.pendingSwitchChildId = null;
        void this.switchActiveChildResults(next);
      } else {
        this.pendingSwitchChildId = null;
      }
    }
  }

  /**
   * Re-run the optimized-assembly asset fetch + engine rebuild pipeline
   * against the currently-pointed-at `projectConfig.project.optimization`
   * URLs. Extracted so it can be called from both initial load (via
   * `switchActiveChildResults`) and future re-run hooks.
   */
  private async reloadOptimizedMeshesForActiveChild(): Promise<void> {
    const engine = this.engine;
    if (!engine) return;

    // Clear per-child cached metrics on the loader. `getOptimizedAssemblyInfo`
    // clears `loadedLoadCases` + `loadCaseUrls` itself, but `frequenciesResult`,
    // `eigenFrequenciesResult`, `freqResponseResult`, and `manufacturingCostResult`
    // are assigned via `=` and persist across calls. If the previous child was
    // vibrations and the new one isn't, those stale arrays would bleed through
    // into the ResultsComponent. Reset them explicitly.
    this.assemblyLoaderService.frequenciesResult = undefined;
    this.assemblyLoaderService.eigenFrequenciesResult = undefined;
    this.assemblyLoaderService.freqResponseResult = undefined;
    this.assemblyLoaderService.eigenFrequenciesPerLc = undefined;
    this.assemblyLoaderService.freqResponsePerLc = undefined;
    this.assemblyLoaderService.manufacturingCostResult = undefined;
    this.assemblyLoaderService.resultsAreVibrations = false;

    // Clear stale reaction forces from the previous child.
    this.reactionForces = undefined;
    this.reactionForcesAvailable = false;

    // Tear down the existing optimized + parametric groups. Remove children
    // from the scene, dispose geometries/materials to avoid GPU leaks.
    const disposeGroup = (group: { children: Array<Record<string, unknown>>; remove: (...c: Array<Record<string, unknown>>) => unknown }) => {
      for (const child of [...(group.children ?? [])]) {
        (child as unknown as { traverse?: (cb: (obj: Record<string, unknown>) => void) => void })
          .traverse?.((obj: Record<string, unknown>) => {
            const mesh = obj as { geometry?: { dispose?: () => void }; material?: unknown };
            mesh.geometry?.dispose?.();
            const mat = mesh.material;
            if (Array.isArray(mat)) {
              for (const m of mat) (m as { dispose?: () => void }).dispose?.();
            } else if (mat) {
              (mat as { dispose?: () => void }).dispose?.();
            }
          });
        group.remove(child);
      }
    };
    try {
      disposeGroup(engine.optimizedGroup as unknown as Parameters<typeof disposeGroup>[0]);
      disposeGroup(engine.optimizedGroupParametric as unknown as Parameters<typeof disposeGroup>[0]);
    } catch (err) {
      console.warn('[Session] Optimized mesh teardown had issues (continuing):', err);
    }

    // Fetch the new child's optimized assembly + parametric + info + reaction
    // forces in parallel. Any that fail are non-fatal.
    //
    // Cache: if we've already fetched these for this childId, reuse the
    // ArrayBuffers + info to avoid the (expensive) binary downloads. The
    // `setOptimizedAssembly` call on the engine still has to re-decode and
    // rebuild the Three.js meshes, but that's an order of magnitude cheaper
    // than the network fetch.
    const cacheKey = this.projectConfigurationService.activeResultsChildId;
    const cached = cacheKey ? this.resultsCacheByChildId.get(cacheKey) : undefined;

    const fetchOrNoop = <T>(
      cachedVal: T | undefined,
      fetcher: () => Promise<T>,
    ): Promise<T> => (cachedVal !== undefined ? Promise.resolve(cachedVal) : fetcher());

    // `info` is intentionally NOT cached — re-running `getOptimizedAssemblyInfo`
    // on every mf-case swap is what clears `loadedLoadCases`, rebuilds
    // `loadCaseUrls`, and recomputes `resultsAreVibrations`. Caching it
    // caused per-loadcase loads to short-circuit against stale keys after
    // the first view (bug H1 in the review).
    const [organic, info, parametric, _reactionForces] = await Promise.allSettled([
      fetchOrNoop(cached?.organic, () => this.getOptimizedAssembly()),
      this.getOptimizedAssemblyInfo(),
      fetchOrNoop(cached?.parametric, () => this.getOptimizedAssemblyParametric()),
      this.getReactionForces(),
    ]);

    // Populate cache for next time — binary buffers only.
    if (cacheKey) {
      const entry = this.resultsCacheByChildId.get(cacheKey) ?? {};
      if (organic.status === 'fulfilled') entry.organic = organic.value;
      if (parametric.status === 'fulfilled') entry.parametric = parametric.value;
      this.resultsCacheByChildId.set(cacheKey, entry);
    }

    if (organic.status === 'fulfilled') {
      await engine.setOptimizedAssembly(organic.value);
      // Add the fresh assemblyOptimized to the group. `initOptimizedMesh`
      // also calls `scene.add(optimizedGroup)` which is a no-op if it's
      // already in the scene from the initial load.
      engine.optimizedGroup.visible = true;
      engine.optimizedGroup.add(engine.assemblyOptimized);
      // If the group is not yet in the scene (first time for a multi-case
      // master where the initial load failed), add it now.
      if (!engine.optimizedGroup.parent) {
        engine.scene.add(engine.optimizedGroup);
      }
      this.organicAssemblyAvailable = true;
    } else {
      this.organicAssemblyAvailable = false;
      engine.optimizedGroup.visible = false;
    }

    if (info.status === 'fulfilled') {
      engine.setOptimizedInfo(info.value);
      this.optimizedInfoAvailable = true;
    } else {
      this.optimizedInfoAvailable = false;
    }

    if (parametric.status === 'fulfilled') {
      await engine.setOptimizedAssemblyParametric(parametric.value);
      engine.optimizedGroupParametric.visible = false;
      engine.optimizedGroupParametric.add(engine.assemblyOptimizedParametric);
      if (!engine.optimizedGroupParametric.parent) {
        engine.scene.add(engine.optimizedGroupParametric);
      }
      this.parametricAssemblyAvailable = true;
    } else {
      this.parametricAssemblyAvailable = false;
      engine.optimizedGroupParametric.visible = false;
    }
  }

  setProject(project: any): void {
    this.projectConfigurationService.setProject(project);

    // New project load: clear any stale dirty flags from the previous session.
    this.clearDirtyCases();

    // New project load: invalidate the per-child results cache from the
    // previous project. Stale ArrayBuffers from a different master would
    // otherwise be reused if childIds collided.
    this.resultsCacheByChildId.clear();
    this.activeResultsMethod = null;
    this.activeResultsHelpers = null;

    // Reset the active-case pointer to the first manufacturing case of the
    // loaded project. Triggers the `activeCase` / `activeView` computed signals
    // to re-evaluate against the newly assigned project object.
    const firstCaseId = this.project?.manufacturingMethods?.[0]?.methodId ?? null;
    this.activeMethodId.set(firstCaseId);
    // Ensure flat scratch-pad mirrors the (now active) first case. For legacy
    // projects lifted by normalizeToNewShape, this is a no-op — the flat fields
    // were the source of that case. For already-new-shape projects loaded from
    // the backend, this populates the flat fields correctly.
    if (firstCaseId) {
      this.projectConfigurationService.hydrateFlatFromCase(firstCaseId);
    }
  }

  /**
   * Switch the active manufacturing case. Commits any pending flat-field
   * edits into the old active case, then hydrates the flat fields from the
   * new target case so the sub-blocks re-render with the new values.
   */
  setActiveCase(methodId: string): void {
    const oldId = this.activeMethodId();
    if (oldId === methodId) return;
    this.projectConfigurationService.commitActiveCaseToArray(oldId);
    this.activeMethodId.set(methodId);
    this.projectConfigurationService.hydrateFlatFromCase(methodId);
    // Suppress the dirty mark on the next auto-save: switching the active
    // case changes the flat scratch-pad fields (which triggers a JSON diff
    // → auto-save), but that's a structural swap, not a user edit.
    this._suppressNextDirtyMark = true;
  }

  /**
   * Add a new manufacturing case and make it active. Returns the new
   * methodId, or null if the addition was rejected (AM already present,
   * max cases reached).
   */
  addManufacturingCase(method: Parameters<ProjectConfigurationService['addCase']>[0]): string | null {
    const oldId = this.activeMethodId();
    this.projectConfigurationService.commitActiveCaseToArray(oldId);
    const newId = this.projectConfigurationService.addCase(method);
    if (newId) {
      this.activeMethodId.set(newId);
      this.projectConfigurationService.hydrateFlatFromCase(newId);
    }
    return newId;
  }

  /**
   * Remove a manufacturing case. If the removed case was active, switches
   * to the first remaining case. Returns true if removal happened.
   */
  removeManufacturingCase(methodId: string): boolean {
    const wasActive = this.activeMethodId() === methodId;
    const removed = this.projectConfigurationService.removeCase(methodId);
    if (removed) {
      // Drop any dirty flag for the removed case so it doesn't leak into
      // subsequent sessions if the user re-adds a case with the same method.
      this.dirtyMethodIds.update((prev) => {
        if (!prev.has(methodId)) return prev;
        const next = new Set(prev);
        next.delete(methodId);
        return next;
      });
      if (wasActive) {
        const fallbackId = this.project?.manufacturingMethods?.[0]?.methodId ?? null;
        this.activeMethodId.set(fallbackId);
        if (fallbackId) {
          this.projectConfigurationService.hydrateFlatFromCase(fallbackId);
        } else {
          // No cases left — clear the singular flat fields so conditional
          // panels that match on `manufacturingMethod === <enum>` hide
          // (Milling Direction, Draw Direction, etc.) and `manufacturingMethodChange`
          // sees `undefined` so the 3D engine helpers (drill bits, draw arrow)
          // are hidden too.
          this.project.manufacturingMethod = undefined as any;
          this.project.millingVector = undefined as any;
          this.project.drawDirectionVector = undefined as any;
        }
      }
    }
    return removed;
  }

  async getProject(): Promise<any> {
    return this.projectConfigurationService.getProject();
  }

  async copyExampleProject(): Promise<{ message: string } | null> {
    return this.projectConfigurationService.copyExampleProject();
  }

  // ─── AssemblyLoaderService delegation ───

  get loadingProject(): boolean {
    return this.assemblyLoaderService.loadingProject;
  }
  set loadingProject(val: boolean) {
    this.assemblyLoaderService.loadingProject = val;
  }

  get loadingProjectStatus(): 'download' | 'init' {
    return this.assemblyLoaderService.loadingProjectStatus;
  }
  set loadingProjectStatus(val: 'download' | 'init') {
    this.assemblyLoaderService.loadingProjectStatus = val;
  }

  get assemblyDownloadProgress(): number {
    return this.assemblyLoaderService.assemblyDownloadProgress;
  }
  set assemblyDownloadProgress(val: number) {
    this.assemblyLoaderService.assemblyDownloadProgress = val;
  }

  get assemblyInitProgress(): { total: number; current: number } {
    return this.assemblyLoaderService.assemblyInitProgress;
  }
  set assemblyInitProgress(val: { total: number; current: number }) {
    this.assemblyLoaderService.assemblyInitProgress = val;
  }

  get parametricAssemblyAvailable(): boolean {
    return this.assemblyLoaderService.parametricAssemblyAvailable;
  }
  set parametricAssemblyAvailable(val: boolean) {
    this.assemblyLoaderService.parametricAssemblyAvailable = val;
  }

  get organicAssemblyAvailable(): boolean {
    return this.assemblyLoaderService.organicAssemblyAvailable;
  }
  set organicAssemblyAvailable(val: boolean) {
    this.assemblyLoaderService.organicAssemblyAvailable = val;
  }

  get optimizedInfoAvailable(): boolean {
    return this.assemblyLoaderService.optimizedInfoAvailable;
  }
  set optimizedInfoAvailable(val: boolean) {
    this.assemblyLoaderService.optimizedInfoAvailable = val;
  }

  get loadCaseDataLoading(): boolean {
    return this.assemblyLoaderService.loadCaseDataLoading;
  }
  set loadCaseDataLoading(val: boolean) {
    this.assemblyLoaderService.loadCaseDataLoading = val;
  }

  get loadCaseDataProgress(): number {
    return this.assemblyLoaderService.loadCaseDataProgress;
  }
  set loadCaseDataProgress(val: number) {
    this.assemblyLoaderService.loadCaseDataProgress = val;
  }

  get loadCaseDataMessage(): string {
    return this.assemblyLoaderService.loadCaseDataMessage;
  }
  set loadCaseDataMessage(val: string) {
    this.assemblyLoaderService.loadCaseDataMessage = val;
  }

  get frequenciesResult(): any {
    return this.assemblyLoaderService.frequenciesResult;
  }
  set frequenciesResult(val: any) {
    this.assemblyLoaderService.frequenciesResult = val;
  }

  get resultsAreVibrations(): boolean {
    return this.assemblyLoaderService.resultsAreVibrations;
  }
  set resultsAreVibrations(val: boolean) {
    this.assemblyLoaderService.resultsAreVibrations = val;
  }

  get resultsAreHarmonicExcitation(): boolean {
    return this.project.loadcases.some((lc) => lc.type === LoadCaseType.HARMONIC_EXCITATION || lc.type === LoadCaseType.HARMONIC_EXCITATION_BASE);
  }

  get resultsAreRandomVibration(): boolean {
    return this.project.loadcases.some((lc) => lc.type === LoadCaseType.RANDOM_VIBRATION || lc.type === LoadCaseType.RANDOM_VIBRATION_BASE);
  }

  /**
   * Eigenfrequencies for the currently selected LC (driven by `engine.currentLoadCase`).
   * Falls back to LC 0 when no LC is selected, and to the flat legacy field when
   * per-LC data isn't loaded (old projects).
   */
  get eigenFrequenciesResult(): { index: number; value: number }[] | undefined {
    const perLc = this.assemblyLoaderService.eigenFrequenciesPerLc;
    if (perLc?.length) {
      const caseId = this.engine?.currentLoadCase ?? null;
      const match = caseId ? perLc.find((entry) => entry.caseId === caseId) : undefined;
      return (match ?? perLc[0]).values;
    }
    return this.assemblyLoaderService.eigenFrequenciesResult;
  }

  /**
   * Frequency response for the currently selected LC. Same fallback chain as
   * `eigenFrequenciesResult`.
   */
  get freqResponseResult(): { index: number; value: number }[] | undefined {
    const perLc = this.assemblyLoaderService.freqResponsePerLc;
    if (perLc?.length) {
      const caseId = this.engine?.currentLoadCase ?? null;
      const match = caseId ? perLc.find((entry) => entry.caseId === caseId) : undefined;
      return (match ?? perLc[0]).values;
    }
    return this.assemblyLoaderService.freqResponseResult;
  }

  /** Per-LC eigenfrequencies (HE/RV multi-load-case). Single-LC and legacy projects yield a one-entry array. */
  get eigenFrequenciesPerLc() {
    return this.assemblyLoaderService.eigenFrequenciesPerLc;
  }

  /** Per-LC frequency response (HE/RV multi-load-case). Single-LC and legacy projects yield a one-entry array. */
  get freqResponsePerLc() {
    return this.assemblyLoaderService.freqResponsePerLc;
  }

  get manufacturingCostResult(): { machiningCost: number } | undefined {
    return this.assemblyLoaderService.manufacturingCostResult;
  }

  getAssembly(protoUrl: string): Promise<ArrayBuffer> {
    return this.assemblyLoaderService.getAssembly(protoUrl);
  }

  async getOptimizedAssembly(): Promise<ArrayBuffer> {
    return this.assemblyLoaderService.getOptimizedAssembly();
  }

  async getOptimizedAssemblyParametric(): Promise<ArrayBuffer> {
    return this.assemblyLoaderService.getOptimizedAssemblyParametric();
  }

  async getOptimizedAssemblyInfo(): Promise<any> {
    return this.assemblyLoaderService.getOptimizedAssemblyInfo();
  }

  async loadLoadCaseAttributes(
    loadcaseId: string,
    type: 'organic' | 'prismatic' = 'organic',
    progressCallback?: (progress: number) => void
  ): Promise<any> {
    return this.assemblyLoaderService.loadLoadCaseAttributes(loadcaseId, type, progressCallback, this.engine);
  }

  isLoadCaseLoaded(loadcaseId: string, type: 'organic' | 'prismatic' = 'organic'): boolean {
    return this.assemblyLoaderService.isLoadCaseLoaded(loadcaseId, type);
  }

  // ─── ReactionForcesService delegation ───

  get reactionForcesAvailable(): boolean {
    return this.reactionForcesService.reactionForcesAvailable;
  }
  set reactionForcesAvailable(val: boolean) {
    this.reactionForcesService.reactionForcesAvailable = val;
  }

  get reactionForces(): { id: string; x: number; y: number; z: number }[][] | undefined {
    return this.reactionForcesService.reactionForces;
  }
  set reactionForces(val: { id: string; x: number; y: number; z: number }[][] | undefined) {
    this.reactionForcesService.reactionForces = val;
  }

  async getReactionForces(): Promise<void> {
    return this.reactionForcesService.getReactionForces();
  }

  // ─── SettingsAndPresetsService delegation ───

  get currentMousePreset(): any {
    return this.settingsAndPresetsService.currentMousePreset;
  }
  set currentMousePreset(val: any) {
    this.settingsAndPresetsService.currentMousePreset = val;
  }

  get currentGraphicsSettings(): any {
    return this.settingsAndPresetsService.currentGraphicsSettings;
  }
  set currentGraphicsSettings(val: any) {
    this.settingsAndPresetsService.currentGraphicsSettings = val;
  }

  public initGraphicsPreset(): void {
    this.settingsAndPresetsService.initGraphicsPreset();
  }

  public getCurrentGraphicsPreset(): void {
    this.settingsAndPresetsService.getCurrentGraphicsPreset();
  }

  public setCurrentGraphicsSettings(): void {
    this.settingsAndPresetsService.setCurrentGraphicsSettings();
  }

  public async changeGraphicsSettings(): Promise<void> {
    return this.settingsAndPresetsService.changeGraphicsSettings();
  }

  public initMousePreset(): void {
    this.settingsAndPresetsService.initMousePreset();
  }

  public getCurrentMousePreset(): void {
    this.settingsAndPresetsService.getCurrentMousePreset();
  }

  public setCurrentMousePreset(): void {
    this.settingsAndPresetsService.setCurrentMousePreset();
  }

  public changeMousePresetSingle(button: any, action: any): void {
    this.settingsAndPresetsService.changeMousePresetSingle(button, action);
  }

  public getSettingBoolean(key: LocalStorageSetting): boolean | null {
    return this.settingsAndPresetsService.getSettingBoolean(key);
  }

  public setSetting(key: LocalStorageSetting, value: string): void {
    this.settingsAndPresetsService.setSetting(key, value);
  }

  async getUserSettings(): Promise<any> {
    return this.settingsAndPresetsService.getUserSettings();
  }

  async setUserSettings(): Promise<any> {
    return this.settingsAndPresetsService.setUserSettings();
  }

  // ─── Methods that remain on SessionService ───

  setAssistantChatComponent(component: AssistantChatComponent): void {
    this.assistantChatComponent = component;
  }

  getAssistantChatComponent(): AssistantChatComponent | undefined {
    return this.assistantChatComponent;
  }

  emitMessage(message: any): void {
    this.pubsub.emit(message);
  }

  guardExampleProject(): boolean {
    if (!this.isExampleProject || this.exampleProjectModalOpen || this.authService.isAdmin()) {
      return false;
    }

    this.exampleProjectModalOpen = true;

    this.modalService
      .openComponent(ExampleProjectCopyModalComponent, { size: 'sm' })
      .afterClosed()
      .subscribe((result: string) => {
        this.exampleProjectModalOpen = false;
        if (result === 'copy') {
          this.pubsub.emit({ type: 'example-project-copy-requested' });
        }
      });

    return true;
  }

  generateId(length: number = 5): string {
    return nanoid(length);
  }

  setEngine(engine: Engine): void {
    this.engine = engine;
    this.settingsAndPresetsService.setEngine(engine);
    this.updateObjectTypes();
    this.startAutoSave();
  }

  updateObjectTypes(): void {
    if (!this.engine) return;

    for (const obj of this.engine.objects) {
      if (this.project.parts.design.includes(obj.data.id)) {
        this.setObjectType(obj, PartType.DESIGN);
      } else if (this.project.parts.nondesign.includes(obj.data.id)) {
        this.setObjectType(obj, PartType.NONDESIGN);
      } else {
        this.setObjectType(obj, PartType.NONE);
      }
    }
  }

  setObjectType(obj: any, type: PartType): void {
    if (!this.engine) return;

    obj.type = type;
    this.engine.selector.setPartColorByType(obj, type);
  }

  // null = no baseline recorded yet (fresh load, first tick seeds it);
  // '' = force-save trigger (part rename/material/visibility setters).
  private lastSavedProject: string | null = null;
  startAutoSave(): void {
    if (this.saveInterval) clearInterval(this.saveInterval);
    if (this.isExampleProject && !this.authService.isAdmin()) return;

    this.saveInterval = setInterval(() => {
      // Suppress auto-save entirely while a Results-page case swap is
      // running. The merge rewrites `project.optimization` + `materials` +
      // `flowVersion` (and the post-merge snapshot differs from the last
      // saved one), which would otherwise trigger a PUT mid-swap. Viewing
      // a child's results is not a user edit.
      if (this.switchingResultsInProgress) return;

      if (this.engine) {
        this.project.parts.design = this.engine.objectsDesign.map((item: any) => item.data.id);
        this.project.parts.nondesign = this.engine.objectsNondesign.map((item: any) => item.data.id);
      }

      const snapshot = JSON.stringify(this.project);
      const action = autoSaveTickAction(snapshot, this.lastSavedProject);
      this.lastSavedProject = snapshot;
      if (action !== 'save') return;
      this.saveProject();
    }, 500);
  }

  async saveProject(): Promise<any> {
    if (this.isExampleProject && !this.authService.isAdmin()) {
      if (!this.exampleProjectInitialSaveCompleted) {
        this.exampleProjectInitialSaveCompleted = true;
        return;
      }
      this.guardExampleProject();
      return;
    }

    try {
      const url = `${environment.API_ADDRESS}/project/${this.project_id}`;

      let body: any;

      // For SoftJaws projects, only save minimal config
      if (this.project.projectType === ProjectType.SoftJaws) {
        body = {
          partNames: this.project.partNames,
          partVisibility: this.project.partVisibility,
          softJaws: this.project.softJaws,
          runType: this.project.runType,
          unitSystem: this.project.unitSystem
        };
      } else if (this.project.projectType === ProjectType.Mold) {
        body = {
          partNames: this.project.partNames,
          partTypes: this.project.partTypes,
          partVisibility: this.project.partVisibility,
          mold: this.project.mold,
          runType: this.project.runType,
          unitSystem: this.project.unitSystem
        };
      } else {
        // For Topology Optimization projects, save full config.
        //
        // Multi-manufacturing-method: commit any pending flat-field edits
        // into the currently-active case before serializing. After commit,
        // the V2 mirror is refreshed from manufacturingMethods[0] so the
        // legacy flat fields in the payload always reflect the FIRST case
        // regardless of which case the user was just editing.
        this.projectConfigurationService.commitActiveCaseToArray(this.activeMethodId());
        const cases = this.project.manufacturingMethods;
        const mirror = cases && cases.length > 0
          ? cases[0]
          : undefined;

        // Mark the active case as dirty if the master already has results
        // from a previous run (runSummary.done >= 1 OR runSummary.total > 0).
        // The badge in the Results case list then reminds the user to re-run.
        // Do nothing for pre-first-run projects — there's nothing to be stale.
        // Also suppress on the first save after project load (structural
        // migration changes, not user edits).
        if (this._suppressNextDirtyMark) {
          this._suppressNextDirtyMark = false;
        } else {
          const runSummary = (this.project as { runSummary?: { done?: number; total?: number } }).runSummary;
          if (runSummary && ((runSummary.done ?? 0) >= 1 || (runSummary.total ?? 0) >= 1)) {
            this.markCaseDirty(this.activeMethodId());
          }
        }

        // Refresh goal volumes (mm³) from the engine. The optimizer's volume
        // fraction spans the whole domain while fractionOfMaterial is a
        // design-space percentage — XML generation converts using these.
        // Never overwrite previously-saved volumes with zeros (engine may not
        // have parts loaded yet during early saves).
        if (this.engine) {
          let designVolume = 0;
          let nonDesignVolume = 0;
          for (const part of this.engine.objects) {
            if (part.type === PartType.DESIGN) {
              designVolume += calculateVolume(part.data.faces);
            } else if (part.type === PartType.NONDESIGN) {
              nonDesignVolume += calculateVolume(part.data.faces);
            }
          }
          if (designVolume + nonDesignVolume > 0) {
            this.project.goal.designVolume = designVolume;
            this.project.goal.nonDesignVolume = nonDesignVolume;
          }
        }

        body = {
          parts: this.project.parts,
          partNames: this.project.partNames,
          partMaterials: this.project.partMaterials,
          partVisibility: this.project.partVisibility,
          materials: this.project.materials,
          loadcases: this.project.loadcases,
          goal: this.project.goal,
          resolution: this.project.resolution,
          symmetry: this.project.symmetry,

          // Multi-manufacturing-method canonical source
          manufacturingMethods: cases ?? undefined,

          // V2 mirror: flat fields denormalized from manufacturingMethods[0].
          // If the project has no cases (shouldn't happen post-normalization,
          // but defensively), fall back to whatever is on this.project.
          manufacturingMethod: mirror?.method ?? this.project.manufacturingMethod ?? 'Additive Manufacturing',
          millingVector: mirror?.millingVector ?? this.project.millingVector,
          drawDirectionVector: mirror?.drawDirectionVector ?? this.project.drawDirectionVector,
          ribsDensity: mirror?.ribsDensity ?? this.project.ribsDensity,
          wallThickness: mirror?.wallThickness ?? this.project.wallThickness,
          bottomCutFrac: mirror?.bottomCutFrac ?? this.project.bottomCutFrac,
          generatePrismatic: mirror?.generatePrismatic ?? this.project.generatePrismatic,
          skeletonMethod: mirror?.skeletonMethod ?? this.project.skeletonMethod,
          costModel: mirror?.costModel ?? this.project.costModel,
          minimumToolSize: mirror?.minimumToolSize ?? this.project.minimumToolSize,
          minimumToolSizeUnit: mirror?.minimumToolSizeUnit ?? this.project.minimumToolSizeUnit,
          numberOfPlanes: this.project.numberOfPlanes,
          extrusionPlaneGeneration: this.project.extrusionPlaneGeneration,

          minimumFeatureSize: this.project.minimumFeatureSize,
          unitSystem: this.project.unitSystem,
          densityUnit: this.project.densityUnit,
          massUnit: this.project.massUnit,
          userCoordinateSystem: this.project.userCoordinateSystem,
          generateOrganic: this.project.generateOrganic,
          stressConstraintEnabled: this.project.stressConstraintEnabled,
          deformationConstraintEnabled: this.project.deformationConstraintEnabled,
          dynamicConstraintType: this.project.dynamicConstraintType,
          runType: this.project.runType,
          // Browser CAD parts
          cadParts: this.project.cadParts,
          userDefinedResolution: this.project.userDefinedResolution,
          solverParameters: this.project.solverParameters
        };
      }

      return lastValueFrom(
        this.http.patch(url, body).pipe(
          catchError((error) => {
            console.error('Error saving project:', error);
            return throwError(() => new Error('Error saving project'));
          })
        )
      );
    } catch (error) {
      console.error('Error saving project:', error);
      throw error;
    }
  }

  async updatePartName(partId: string, newName: string): Promise<void> {
    if (!newName || !newName.trim()) {
      return;
    }

    // Initialize partNames if it doesn't exist
    if (!this.project.partNames) {
      this.project.partNames = {};
    }

    // Update the part name in the project
    this.project.partNames[partId] = newName.trim();

    // Update the engine to show the new name
    if (this.engine) {
      this.engine.updatePartNames(this.project.partNames);
    }

    // Trigger auto-save
    this.lastSavedProject = '';
  }

  async updatePartMaterial(partId: string, materialId: string): Promise<void> {
    if (!materialId) {
      return;
    }

    // Initialize partMaterials if it doesn't exist
    if (!this.project.partMaterials) {
      this.project.partMaterials = {};
    }

    // Update the part material in the project
    this.project.partMaterials[partId] = materialId;

    // Trigger auto-save
    this.lastSavedProject = '';
  }

  /**
   * Set part visibility and persist to project
   */
  setPartVisibility(partId: string, visible: boolean): void {
    // Initialize partVisibility if it doesn't exist
    if (!this.project.partVisibility) {
      this.project.partVisibility = {};
    }

    // Update visibility in project (only store hidden parts to save space)
    if (visible) {
      delete this.project.partVisibility[partId];
    } else {
      this.project.partVisibility[partId] = false;
    }

    // Update the engine
    if (this.engine) {
      const obj = this.engine.objects.find((o: any) => o.data.id === partId);
      if (obj?.group) {
        obj.group.visible = visible;
      }
    }

    // Trigger auto-save
    this.lastSavedProject = '';
  }

  /**
   * Apply saved visibility settings to engine objects
   * Called after engine is initialized
   */
  applyPartVisibility(): void {
    if (!this.project.partVisibility || !this.engine) return;

    for (const [partId, visible] of Object.entries(this.project.partVisibility)) {
      const obj = this.engine.objects.find((o: any) => o.data.id === partId);
      if (obj?.group) {
        obj.group.visible = visible;
      }
    }
  }

  async generateShapeCAD(shape: {
    id: string;
    name: string;
    points: number[][];
    holes?: number[][][];
    extrude_height: number;
    color?: string;
    z_offset?: number;
    geometryType?: 'polygon' | 'circle' | 'ring';
    geometryParams?:
      | { center: [number, number, number]; radius: number }
      | { center: [number, number, number]; outerRadius: number; innerRadius: number };
  }): Promise<void> {
    const response = await lastValueFrom(
      this.http.post(`${environment.API_ADDRESS}/project/${this.project_id}/generate-shape-cad`, {
        shape: {
          id: shape.id,
          name: shape.name,
          points: shape.points,
          holes: shape.holes,
          extrude_height: shape.extrude_height,
          color: shape.color,
          z_offset: shape.z_offset || 0,
          geometryType: shape.geometryType,
          geometryParams: shape.geometryParams
        }
      })
    );
    return response as any;
  }

  /**
   * Upload a CAD part generated in browser using OpenCascade.js
   * Uploads STEP file to S3 and triggers Lambda for processing
   * @deprecated Use batchUploadBrowserCADParts for multiple parts to avoid race conditions
   */
  async uploadBrowserCADPart(params: {
    partId: string;
    partName: string;
    stepBlob: Blob;
    color?: string;
  }): Promise<{ partId: string; stepKey: string }> {
    // Convert blob to base64
    const arrayBuffer = await params.stepBlob.arrayBuffer();
    const base64 = btoa(new Uint8Array(arrayBuffer).reduce((data, byte) => data + String.fromCharCode(byte), ''));

    const response = await lastValueFrom(
      this.http.post(`${environment.API_ADDRESS}/project/${this.project_id}/browser-cad/upload`, {
        partId: params.partId,
        partName: params.partName,
        stepData: base64,
        color: params.color
      })
    );

    return response as { partId: string; stepKey: string };
  }

  /**
   * Batch upload multiple CAD parts generated in browser using OpenCascade.js
   * Uploads all STEP files to S3 and triggers single Lambda for batch processing
   * This avoids race conditions when multiple parts update assembly.proto
   */
  async batchUploadBrowserCADParts(
    parts: Array<{
      partId: string;
      partName: string;
      stepBlob: Blob;
      color?: string;
    }>
  ): Promise<{ partsCount: number; partIds: string[] }> {
    // Convert all blobs to base64
    const partsData = await Promise.all(
      parts.map(async (part) => {
        const arrayBuffer = await part.stepBlob.arrayBuffer();
        const base64 = btoa(new Uint8Array(arrayBuffer).reduce((data, byte) => data + String.fromCharCode(byte), ''));
        return {
          partId: part.partId,
          partName: part.partName,
          stepData: base64,
          color: part.color
        };
      })
    );

    const response = await lastValueFrom(
      this.http.post(`${environment.API_ADDRESS}/project/${this.project_id}/browser-cad/batch-upload`, {
        parts: partsData
      })
    );

    return response as { partsCount: number; partIds: string[] };
  }

  async updatePartMoldType(partId: string, moldTypeId: 'design_domain_mold' | 'hallowed_mold'): Promise<void> {
    if (!moldTypeId) {
      return;
    }

    // Update the part mold type in the project
    if (!this.project.partTypes) {
      this.project.partTypes = {};
    }
    this.project.partTypes[partId] = moldTypeId;
  }

  /**
   * Remove a part from partTypes mapping
   */
  removePartType(partId: string): void {
    if (this.project.partTypes && this.project.partTypes[partId]) {
      delete this.project.partTypes[partId];
    }
  }

  /**
   * Clean up partTypes that reference parts that no longer exist in the engine
   * Call this after assembly is loaded or parts are removed
   */
  cleanupOrphanedPartTypes(): void {
    if (!this.project.partTypes || !this.engine) return;

    const validPartIds = new Set(this.engine.selector.objects.map((obj: any) => obj.data?.id).filter(Boolean));

    const orphanedIds: string[] = [];
    for (const partId of Object.keys(this.project.partTypes)) {
      if (!validPartIds.has(partId)) {
        orphanedIds.push(partId);
      }
    }

    if (orphanedIds.length > 0) {
      for (const partId of orphanedIds) {
        delete this.project.partTypes[partId];
      }
    }
  }

  setFlowStep(flowStep: FlowStep): void {
    this.engine!.selector.notAllowedPartTypes = [PartType.DESIGN];
    this.flowStep = flowStep;
    this.pubsub.emit({ type: 'flow-step-changed', step: flowStep });
  }

  async sendFeedback(message: string): Promise<any> {
    let url = `${environment.API_ADDRESS}/project/feedback`;

    return this.http.post(url, { message });
  }

  // User local storage functions
  // Function to save user step data to localStorage
  saveUserStep(projectId: string, flowStep: FlowStep): void {
    // Retrieve the current data from localStorage or initialize a new Map
    const data = localStorage.getItem('userProjectStep');
    const userProjectStep = data ? new Map<string, FlowStep>(JSON.parse(data)) : new Map<string, FlowStep>();

    // Update the step for the given userId
    userProjectStep.set(projectId, flowStep);

    // Save the updated map back to localStorage as a JSON string
    localStorage.setItem('userProjectStep', JSON.stringify(Array.from(userProjectStep.entries())));
  }

  // Function to retrieve user step by userId
  getUserStep(projectId: string): FlowStep | undefined {
    // Retrieve the data from localStorage and parse it
    const data = localStorage.getItem('userProjectStep');
    if (!data) return undefined;

    const userProjectStep = new Map<string, FlowStep>(JSON.parse(data));
    return userProjectStep.get(projectId);
  }
}

export enum LocalStorageSetting {
  LAYOUT_HELPERS = 'LAYOUT_HELPERS',
  LAYOUT_GRID = 'LAYOUT_GRID',
  LAYOUT_DIMENSIONS = 'LAYOUT_DIMENSIONS'
}
